import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["className", "children"],
  _excluded2 = ["className", "children"],
  _excluded3 = ["aiText", "aiTextLabel", "textLabel", "align", "autoAlign", "children", "className", "kind", "onRevertClick", "revertActive", "revertLabel", "slugLabel", "aria-label", "size"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import deprecateValuesWithin from '../../prop-types/deprecateValuesWithin.js';
import React from 'react';
import { usePrefix } from '../../internal/usePrefix.js';
import { ToggletipContent, ToggletipActions, Toggletip, ToggletipButton } from '../Toggletip/index.js';
import { IconButton } from '../IconButton/index.js';
import { mapPopoverAlign } from '../../tools/mapPopoverAlign.js';
import { Undo } from '@carbon/icons-react';
import { useId } from '../../internal/useId.js';
import { deprecate } from '../../prop-types/deprecate.js';
var _Undo;
var AILabelContent = /*#__PURE__*/React.forwardRef(function AILabelContent(_ref, ref) {
  var _cx;
  var className = _ref.className,
    children = _ref.children,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var hasAILabelActions = React.Children.toArray(children).some(function (child) {
    var item = child;
    // TODO: Is there supposed to be a `return` here? If so, this issue would
    // have been caught by ESLint. It's concerning that this code is 7 months
    // old and no one has noticed any issues with it. It also makes me question
    // whether the code is necessary.
    // https://github.com/carbon-design-system/carbon/issues/18991
    item.type === AILabelActions;
  });
  var aiLabelContentClasses = cx(className, (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--ai-label-content"), true), _defineProperty(_cx, "".concat(prefix, "--ai-label-content--with-actions"), hasAILabelActions), _cx));
  return /*#__PURE__*/React.createElement(ToggletipContent, {
    className: aiLabelContentClasses
  }, children);
});
AILabelContent.displayName = 'AILabelContent';
var AILabelActions = /*#__PURE__*/React.forwardRef(function AILabelActions(_ref2, ref) {
  var className = _ref2.className,
    children = _ref2.children,
    rest = _objectWithoutProperties(_ref2, _excluded2);
  var prefix = usePrefix();
  var aiLabelActionsClasses = cx(className, _defineProperty({}, "".concat(prefix, "--ai-label-actions"), true));
  return /*#__PURE__*/React.createElement(ToggletipActions, {
    className: aiLabelActionsClasses
  }, children);
});
AILabelActions.displayName = 'AILabelActions';
/**
 * Deprecated popover alignment values.
 * @deprecated Use NewPopoverAlignment instead.
 */

var AILabel = /*#__PURE__*/React.forwardRef(function AILabel(_ref3, ref) {
  var _cx3, _cx4;
  var _ref3$aiText = _ref3.aiText,
    aiText = _ref3$aiText === void 0 ? 'AI' : _ref3$aiText,
    aiTextLabel = _ref3.aiTextLabel,
    textLabel = _ref3.textLabel,
    align = _ref3.align,
    _ref3$autoAlign = _ref3.autoAlign,
    autoAlign = _ref3$autoAlign === void 0 ? true : _ref3$autoAlign,
    children = _ref3.children,
    className = _ref3.className,
    _ref3$kind = _ref3.kind,
    kind = _ref3$kind === void 0 ? 'default' : _ref3$kind,
    onRevertClick = _ref3.onRevertClick,
    revertActive = _ref3.revertActive,
    _ref3$revertLabel = _ref3.revertLabel,
    revertLabel = _ref3$revertLabel === void 0 ? 'Revert to AI input' : _ref3$revertLabel,
    _ref3$slugLabel = _ref3.slugLabel,
    slugLabel = _ref3$slugLabel === void 0 ? 'Show information' : _ref3$slugLabel,
    _ref3$ariaLabel = _ref3['aria-label'],
    ariaLabel = _ref3$ariaLabel === void 0 ? 'Show information' : _ref3$ariaLabel,
    _ref3$size = _ref3.size,
    size = _ref3$size === void 0 ? 'xs' : _ref3$size,
    rest = _objectWithoutProperties(_ref3, _excluded3);
  var prefix = usePrefix();
  var id = useId('AILabel');
  var aiLabelClasses = cx(className, (_cx3 = {}, _defineProperty(_cx3, "".concat(prefix, "--ai-label"), true), _defineProperty(_cx3, "".concat(prefix, "--ai-label--revert"), revertActive), _cx3));
  var aiLabelButtonClasses = cx((_cx4 = {}, _defineProperty(_cx4, "".concat(prefix, "--ai-label__button"), true), _defineProperty(_cx4, "".concat(prefix, "--ai-label__button--").concat(size), size), _defineProperty(_cx4, "".concat(prefix, "--ai-label__button--").concat(kind), kind), _defineProperty(_cx4, "".concat(prefix, "--ai-label__button--inline-with-content"), kind === 'inline' && (aiTextLabel || textLabel)), _cx4));
  var handleOnRevertClick = function handleOnRevertClick(evt) {
    if (onRevertClick) {
      onRevertClick(evt);
    }
  };
  var ariaLabelText = !aiTextLabel && !textLabel ? "".concat(aiText, " ").concat(slugLabel || ariaLabel) : "".concat(aiText, " ").concat(aiTextLabel || textLabel);
  var isSmallIcon = ['xs', '2xs', 'mini'].includes(size);
  return /*#__PURE__*/React.createElement("div", {
    className: aiLabelClasses,
    ref: ref,
    id: id
  }, revertActive ? /*#__PURE__*/React.createElement(IconButton, _extends({
    onClick: handleOnRevertClick,
    kind: "ghost",
    size: "sm",
    label: revertLabel
  }, rest), _Undo || (_Undo = /*#__PURE__*/React.createElement(Undo, null))) : /*#__PURE__*/React.createElement(Toggletip, _extends({
    align: align,
    autoAlign: autoAlign,
    alignmentAxisOffset: isSmallIcon ? -24 : 0
  }, rest), /*#__PURE__*/React.createElement(ToggletipButton, {
    className: aiLabelButtonClasses,
    label: kind === 'inline' ? '' : ariaLabelText
  }, /*#__PURE__*/React.createElement("span", {
    className: "".concat(prefix, "--ai-label__text")
  }, aiText), kind === 'inline' && (aiTextLabel || textLabel) && /*#__PURE__*/React.createElement("span", {
    className: "".concat(prefix, "--ai-label__additional-text")
  }, aiTextLabel || textLabel)), children));
});
AILabel.displayName = 'AILabel';
export { AILabel, AILabelActions, AILabelContent };